import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, MapPin } from 'lucide-react';
import LandingLayout from '../components/LandingLayout';
import { certificatesApi, CertificateVerificationResponse } from '../api/certificates';

export default function VerifyCertificatePage() {
  const { code } = useParams<{ code: string }>();
  const { t, i18n } = useTranslation();
  const [result, setResult] = useState<CertificateVerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError('No code provided');
      setLoading(false);
      return;
    }

    certificatesApi
      .verifyCertificate(code)
      .then(setResult)
      .catch(() => setError('Failed to verify certificate'))
      .finally(() => setLoading(false));
  }, [code]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'active':
        return t('certificate.status.active');
      case 'trialing':
        return t('certificate.status.trialing');
      default:
        return t('certificate.status.inactive');
    }
  };

  return (
    <LandingLayout>
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="max-w-md w-full mx-auto">
          {loading ? (
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
              <p className="text-gray-400">{t('certificate.verifying')}</p>
            </div>
          ) : error ? (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('certificate.verificationFailed')}
              </h1>
              <p className="text-gray-400">{error}</p>
            </div>
          ) : result?.valid ? (
            <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-white">
                  {t('certificate.validCertificate')}
                </h1>
              </div>

              <div className="space-y-4">
                <div className="bg-dark-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">
                    {t('certificate.venueName')}
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {result.venueName || '—'}
                  </p>
                </div>

                {result.venueAddress && (
                  <div className="bg-dark-900/50 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-1 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {t('certificate.venueAddress')}
                    </p>
                    <p className="text-white">{result.venueAddress}</p>
                  </div>
                )}

                <div className="bg-dark-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">
                    {t('certificate.subscriptionStatus')}
                  </p>
                  <p className="text-lg font-semibold text-green-400">
                    {getStatusLabel(result.subscriptionStatus)}
                  </p>
                </div>

                <div className="bg-dark-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">
                    {t('certificate.validityPeriod')}
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {formatDate(result.periodStart)} — {formatDate(result.periodEnd)}
                  </p>
                </div>

                <div className="bg-dark-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">
                    {t('certificate.email')}
                  </p>
                  <p className="text-white">{result.email}</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center mt-6">
                {t('certificate.verifiedBy')}
              </p>
            </div>
          ) : (
            <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-white">
                  {t('certificate.invalidCertificate')}
                </h1>
                <p className="text-gray-400 mt-2">
                  {result?.reason || t('certificate.noActiveSubscription')}
                </p>
              </div>

              {result?.venueName && (
                <div className="bg-dark-900/50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-400 mb-1">
                    {t('certificate.venueName')}
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {result.venueName}
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-400 text-center">
                {t('certificate.expiredNote')}
              </p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/"
              className="text-primary-400 hover:text-primary-300 text-sm transition-colors"
            >
              {t('certificate.learnMore')}
            </Link>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
